/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Mail,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List as ListIcon,
  Link as LinkIcon,
  Loader2,
  Image as ImageIcon,
  Table,
  Paintbrush,
  ListOrdered,
} from "lucide-react";

const EmailDialog = ({ isOpen, onClose, onSend, recipientCount }) => {
  const [subject, setSubject] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const contentEditableRef = useRef(null);
  const [emailContent, setEmailContent] = useState(
    "Dear Applicant,<br><br>We are pleased to inform you...",
  );
  const [fontSize, setFontSize] = useState("normal");
  const [fontFamily, setFontFamily] = useState("sans-serif");
  const [textColor, setTextColor] = useState("#000000");
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSubject("");
      setEmailContent("Dear Applicant,<br><br>We are pleased to inform you...");
      setFontSize("normal");
      setFontFamily("sans-serif");
      setTextColor("#000000");
      setIsLoading(false);
      setShowColorPicker(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (!isLoading) {
      // Clear state when closing
      setSubject("");
      setEmailContent("Dear Applicant,<br><br>We are pleased to inform you...");
      onClose();
    }
  };

  const handleDiscard = () => {
    if (!isLoading) {
      // Clear state when discarding
      setSubject("");
      setEmailContent("Dear Applicant,<br><br>We are pleased to inform you...");
      onClose();
    }
  };

  const handleSend = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      await onSend({ subject, content: emailContent });
      // Clear state after successful send
      setSubject("");
      setEmailContent("Dear Applicant,<br><br>We are pleased to inform you...");
    } catch (error) {
      // Keep state if there's an error
    } finally {
      setIsLoading(false);
    }
  };

  // Rich text editor functions
  const applyBold = () => {
    document.execCommand("bold", false, null);
    updateContent();
  };

  const applyItalic = () => {
    document.execCommand("italic", false, null);
    updateContent();
  };

  const applyUnderline = () => {
    document.execCommand("underline", false, null);
    updateContent();
  };

  const alignLeft = () => {
    document.execCommand("justifyLeft", false, null);
    updateContent();
  };

  const alignCenter = () => {
    document.execCommand("justifyCenter", false, null);
    updateContent();
  };

  const alignRight = () => {
    document.execCommand("justifyRight", false, null);
    updateContent();
  };

  const insertUnorderedList = () => {
    document.execCommand("insertUnorderedList", false, null);
    updateContent();
  };

  const insertOrderedList = () => {
    document.execCommand("insertOrderedList", false, null);
    updateContent();
  };

  const insertLink = () => {
    const url = prompt("Enter the URL:");
    if (url) {
      document.execCommand("createLink", false, url);
      updateContent();
    }
  };

  const insertImage = () => {
    const url = prompt("Enter image URL:");
    if (url) {
      document.execCommand("insertImage", false, url);
      updateContent();
    }
  };

  const insertTable = () => {
    const rows = prompt("Enter number of rows:", "2");
    const cols = prompt("Enter number of columns:", "2");

    if (rows && cols) {
      let tableHtml = "<table style='border-collapse: collapse; width: 100%;'>";
      for (let i = 0; i < rows; i++) {
        tableHtml += "<tr>";
        for (let j = 0; j < cols; j++) {
          tableHtml += `<td style='border: 1px solid #ccc; padding: 8px;'>&nbsp;</td>`;
        }
        tableHtml += "</tr>";
      }
      tableHtml += "</table>";

      document.execCommand("insertHTML", false, tableHtml);
      updateContent();
    }
  };

  const changeFontSize = (size) => {
    setFontSize(size);
    if (size === "h1") {
      document.execCommand("formatBlock", false, "<h1>");
    } else if (size === "h2") {
      document.execCommand("formatBlock", false, "<h2>");
    } else {
      document.execCommand("formatBlock", false, "<div>");
      document.execCommand("fontSize", false, "3"); // Normal size
    }
    updateContent();
  };

  const changeFontFamily = (family) => {
    setFontFamily(family);
    document.execCommand("fontName", false, family);
    updateContent();
  };

  const changeTextColor = (color) => {
    setTextColor(color);
    document.execCommand("foreColor", false, color);
    setShowColorPicker(false);
    updateContent();
  };

  const updateContent = () => {
    if (contentEditableRef.current) {
      setEmailContent(contentEditableRef.current.innerHTML);
    }
  };

  const handleEditorInput = () => {
    updateContent();
  };

  // Check if a command is active
  const isCommandActive = (command) => {
    return document.queryCommandState(command);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-2xl animate-in fade-in zoom-in-95 duration-200">
        <div className="p-6 border-b border-gray-100 dark:border-dark-border flex justify-between items-center">
          <h3 className="text-lg font-bold text-text dark:text-dark-text flex items-center gap-2">
            <Mail size={20} className="text-primary" /> Send Email
            <span className="text-xs font-normal text-gray-500 dark:text-dark-muted bg-accent dark:bg-dark-border px-2 py-0.5 rounded-full ml-2">
              To: {recipientCount} Recipients
            </span>
          </h3>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className={`${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <X size={20} className="text-gray-400 dark:text-dark-muted" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <input
            type="text"
            placeholder="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={isLoading}
            className="w-full px-4 py-2 text-lg font-bold border-b border-gray-200 dark:border-dark-border bg-transparent outline-none placeholder-gray-400 dark:placeholder-dark-muted text-text dark:text-dark-text disabled:opacity-50 disabled:cursor-not-allowed"
          />

          {/* Rich Text Toolbar */}
          <div className="flex flex-wrap gap-2 border border-gray-200 dark:border-dark-border rounded-t-lg p-2 bg-accent dark:bg-dark-background">
            {/* Font Size */}
            <div className="relative">
              <select
                value={fontSize}
                onChange={(e) => changeFontSize(e.target.value)}
                disabled={isLoading}
                className="px-2 py-1 text-xs bg-card dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded outline-none focus:ring-2 focus:ring-primary text-text dark:text-dark-text disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="normal">Normal</option>
                <option value="h1">Heading 1</option>
                <option value="h2">Heading 2</option>
              </select>
            </div>

            {/* Font Family */}
            <div className="relative">
              <select
                value={fontFamily}
                onChange={(e) => changeFontFamily(e.target.value)}
                disabled={isLoading}
                className="px-2 py-1 text-xs bg-card dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded outline-none focus:ring-2 focus:ring-primary text-text dark:text-dark-text disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="sans-serif">Sans Serif</option>
                <option value="serif">Serif</option>
                <option value="monospace">Monospace</option>
              </select>
            </div>

            {/* Text Color */}
            <div className="relative">
              <button
                onClick={() => setShowColorPicker(!showColorPicker)}
                disabled={isLoading}
                className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-dark-border text-gray-500 dark:text-dark-muted disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                title="Text Color"
              >
                <Paintbrush size={14} />
                <div
                  className="w-3 h-3 rounded-sm border border-gray-300 dark:border-dark-border"
                  style={{ backgroundColor: textColor }}
                />
              </button>

              {showColorPicker && (
                <div className="absolute left-0 top-full mt-1 bg-card dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg shadow-lg p-2 z-20 grid grid-cols-6 gap-1">
                  {[
                    "#000000",
                    "#FF0000",
                    "#00FF00",
                    "#0000FF",
                    "#FFFF00",
                    "#FF00FF",
                    "#00FFFF",
                    "#FFA500",
                    "#800080",
                    "#008000",
                    "#800000",
                    "#008080",
                    "#000080",
                    "#808080",
                    "#C0C0C0",
                    "#FFFFFF",
                  ].map((color) => (
                    <button
                      key={color}
                      onClick={() => changeTextColor(color)}
                      className="w-6 h-6 rounded border border-gray-300 dark:border-dark-border hover:scale-110 transition-transform"
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Formatting Buttons */}
            <div className="flex items-center gap-1 border-l border-gray-200 dark:border-dark-border pl-2">
              <button
                onClick={applyBold}
                disabled={isLoading}
                className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-dark-border ${
                  isCommandActive("bold")
                    ? "bg-primary/20 text-primary dark:bg-primary/30"
                    : "text-gray-500 dark:text-dark-muted"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                title="Bold"
              >
                <Bold size={14} />
              </button>

              <button
                onClick={applyItalic}
                disabled={isLoading}
                className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-dark-border ${
                  isCommandActive("italic")
                    ? "bg-primary/20 text-primary dark:bg-primary/30"
                    : "text-gray-500 dark:text-dark-muted"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                title="Italic"
              >
                <Italic size={14} />
              </button>

              <button
                onClick={applyUnderline}
                disabled={isLoading}
                className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-dark-border ${
                  isCommandActive("underline")
                    ? "bg-primary/20 text-primary dark:bg-primary/30"
                    : "text-gray-500 dark:text-dark-muted"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                title="Underline"
              >
                <Underline size={14} />
              </button>
            </div>

            {/* Alignment Buttons */}
            <div className="flex items-center gap-1 border-l border-gray-200 dark:border-dark-border pl-2">
              <button
                onClick={alignLeft}
                disabled={isLoading}
                className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-dark-border ${
                  isCommandActive("justifyLeft")
                    ? "bg-primary/20 text-primary dark:bg-primary/30"
                    : "text-gray-500 dark:text-dark-muted"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                title="Align Left"
              >
                <AlignLeft size={14} />
              </button>

              <button
                onClick={alignCenter}
                disabled={isLoading}
                className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-dark-border ${
                  isCommandActive("justifyCenter")
                    ? "bg-primary/20 text-primary dark:bg-primary/30"
                    : "text-gray-500 dark:text-dark-muted"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                title="Align Center"
              >
                <AlignCenter size={14} />
              </button>

              <button
                onClick={alignRight}
                disabled={isLoading}
                className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-dark-border ${
                  isCommandActive("justifyRight")
                    ? "bg-primary/20 text-primary dark:bg-primary/30"
                    : "text-gray-500 dark:text-dark-muted"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                title="Align Right"
              >
                <AlignRight size={14} />
              </button>
            </div>

            {/* List Buttons */}
            <div className="flex items-center gap-1 border-l border-gray-200 dark:border-dark-border pl-2">
              <button
                onClick={insertUnorderedList}
                disabled={isLoading}
                className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-dark-border ${
                  isCommandActive("insertUnorderedList")
                    ? "bg-primary/20 text-primary dark:bg-primary/30"
                    : "text-gray-500 dark:text-dark-muted"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                title="Bullet List"
              >
                <ListIcon size={14} />
              </button>

              <button
                onClick={insertOrderedList}
                disabled={isLoading}
                className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-dark-border ${
                  isCommandActive("insertOrderedList")
                    ? "bg-primary/20 text-primary dark:bg-primary/30"
                    : "text-gray-500 dark:text-dark-muted"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                title="Numbered List"
              >
                <ListOrdered size={14} />
              </button>
            </div>

            {/* Insert Buttons */}
            <div className="flex items-center gap-1 border-l border-gray-200 dark:border-dark-border pl-2">
              <button
                onClick={insertLink}
                disabled={isLoading}
                className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-dark-border text-gray-500 dark:text-dark-muted disabled:opacity-50 disabled:cursor-not-allowed"
                title="Insert Link"
              >
                <LinkIcon size={14} />
              </button>

              <button
                onClick={insertImage}
                disabled={isLoading}
                className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-dark-border text-gray-500 dark:text-dark-muted disabled:opacity-50 disabled:cursor-not-allowed"
                title="Insert Image"
              >
                <ImageIcon size={14} />
              </button>

              <button
                onClick={insertTable}
                disabled={isLoading}
                className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-dark-border text-gray-500 dark:text-dark-muted disabled:opacity-50 disabled:cursor-not-allowed"
                title="Insert Table"
              >
                <Table size={14} />
              </button>
            </div>
          </div>

          {/* Rich Text Editor */}
          <div
            ref={contentEditableRef}
            contentEditable
            onInput={handleEditorInput}
            dangerouslySetInnerHTML={{ __html: emailContent }}
            disabled={isLoading}
            className="w-full min-h-[200px] p-4 border border-gray-200 dark:border-dark-border border-t-0 rounded-b-lg outline-none focus:ring-2 focus:ring-primary bg-accent dark:bg-dark-border text-text dark:text-dark-text overflow-y-auto disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              fontFamily: fontFamily,
              color: textColor,
            }}
            placeholder="Compose your email here..."
          />
        </div>
        <div className="p-6 pt-2 flex justify-end gap-3 border-t border-gray-100 dark:border-dark-border">
          <button
            onClick={handleDiscard}
            disabled={isLoading}
            className="px-6 py-2.5 bg-card border border-gray-200 dark:bg-dark-surface dark:border-dark-border text-text dark:text-dark-text rounded-xl font-medium hover:bg-accent dark:hover:bg-dark-border transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Discard
          </button>
          <button
            onClick={handleSend}
            disabled={isLoading}
            className="px-8 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold transition-colors shadow-lg shadow-primary/20 dark:shadow-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Sending...
              </>
            ) : (
              "Send Email"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailDialog;
